import { useState } from 'react'
import { useNavigate, useParams, Outlet } from 'react-router-dom'
import { GripVertical } from 'lucide-react'

import { cn } from '@/common/lib/utils'
import { useIsMobile } from '@/common/hooks/useMobile'

import { RoomList } from '../RoomList'
import { ChatRoomEmpty } from '../ChatRoom'

interface ChatLayoutProps {
	className?: string
}

const MIN_PANEL_WIDTH = 280
const MAX_PANEL_WIDTH = 600
const DEFAULT_PANEL_WIDTH = 300

export function ChatLayout({ className }: ChatLayoutProps) {
	const navigate = useNavigate()
	const { roomId } = useParams<{ roomId: string }>()
	const isMobile = useIsMobile()
	const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH)
	const [isDragging, setIsDragging] = useState(false)

	const handleSelectRoom = (selectedRoomId: string) => {
		navigate(`/operator/chat/${selectedRoomId}`)
	}

	const handleMouseDown = (e: React.MouseEvent) => {
		e.preventDefault()
		setIsDragging(true)

		const startX = e.clientX
		const startWidth = panelWidth

		const handleMouseMove = (e: MouseEvent) => {
			const deltaX = e.clientX - startX
			const newWidth = Math.max(
				MIN_PANEL_WIDTH,
				Math.min(MAX_PANEL_WIDTH, startWidth + deltaX),
			)
			setPanelWidth(newWidth)
		}

		const handleMouseUp = () => {
			setIsDragging(false)
			document.removeEventListener('mousemove', handleMouseMove)
			document.removeEventListener('mouseup', handleMouseUp)
		}

		document.addEventListener('mousemove', handleMouseMove)
		document.addEventListener('mouseup', handleMouseUp)
	}

	// Mobile: Show only room list or chat room based on route
	if (isMobile) {
		return (
			<div
				className={cn(
					'flex h-[calc(100dvh-theme(spacing.20))] overflow-hidden rounded-lg border bg-background',
					className,
				)}
			>
				{roomId ? (
					// Mobile: Show chat room (via Outlet)
					<div className="min-w-0 flex-1 overflow-hidden">
						<Outlet />
					</div>
				) : (
					// Mobile: Show room list
					<div className="min-w-0 flex-1 overflow-hidden">
						<RoomList selectedRoomId={null} onSelectRoom={handleSelectRoom} />
					</div>
				)}
			</div>
		)
	}

	// Desktop: Show both room list and chat room side by side
	return (
		<div
			className={cn(
				'flex h-[calc(100vh-theme(spacing.20))] overflow-hidden rounded-lg border bg-background',
				isDragging && 'select-none',
				className,
			)}
		>
			{/* Room List - Resizable width */}
			<div className="shrink-0 overflow-hidden" style={{ width: panelWidth }}>
				<RoomList
					selectedRoomId={roomId ?? null}
					onSelectRoom={handleSelectRoom}
				/>
			</div>

			{/* Resize Handle */}
			<div
				onMouseDown={handleMouseDown}
				className={cn(
					'relative flex w-1 shrink-0 cursor-col-resize items-center justify-center bg-border transition-colors hover:bg-primary/20',
					'after:absolute after:inset-y-0 after:left-1/2 after:w-3 after:-translate-x-1/2',
					isDragging && 'bg-primary/30',
				)}
			>
				<div className="z-10 flex h-8 w-4 items-center justify-center rounded-sm border bg-background shadow-sm hover:bg-muted">
					<GripVertical className="h-4 w-4 text-muted-foreground" />
				</div>
			</div>

			{/* Chat Room - Flexible width */}
			<div className="min-w-0 flex-1 overflow-hidden">
				{roomId ? <Outlet /> : <ChatRoomEmpty />}
			</div>
		</div>
	)
}
